import { MenuColors } from '../utils/menuColors';
import axios from 'axios';

const getApiBaseUrl = () => {
  const isDevelopment = process.env.NODE_ENV === 'development' ||
                       window.location.hostname === 'localhost' ||
                       window.location.hostname === '127.0.0.1' ||
                       window.location.port === '3002';

  const baseUrl = isDevelopment
    ? (process.env.REACT_APP_API_URL_LOCAL || 'http://localhost:8080')
    : (process.env.REACT_APP_API_URL_PROD || 'https://backend.mvpdelivery.com');

  return `${baseUrl}/api`;
};

const API_BASE_URL = getApiBaseUrl();

const cardapioApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface Store {
  id: string;
  name: string;
  endereco?: string;
  meios_pagamento?: string;
  cobrar_preco_maior?: boolean;
  hora_fechamento?: string;
  pedido_minimo?: number;
  instagram_link?: string;
  facebook_link?: string;
  twitter_link?: string;
  image_path?: string;
  banner_image_path?: string;
  cores_cardapio?: MenuColors | null;
  pix_mp_enabled?: boolean;
}

export interface CardapioLinkValidation {
  valid: boolean;
  mode: 'cardapio';
  store?: Store;
  error?: string;
}

export interface CardapioRedirectResponse {
  success: boolean;
  link_id?: string;
  redirect_path?: string;
  error?: string;
}

export interface ProductTypeInput {
  id: string;
  product_type_id: string;
  name: string;
  input_type: 'string' | 'checkbox' | 'dropdown' | 'fixed_checkbox';
  type_description?: string;
  values?: string[];
  preco_diferente?: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProductType {
  id: string;
  type: string;
  attributes: string[];
  product_type_inputs: ProductTypeInput[];
  created_at: string;
  updated_at: string;
}

export interface Product {
  id: string;
  name: string;
  price: number;
  user_id: string;
  product_type_id?: string | null;
  product_type?: ProductType;
  attributes: Record<string, any>;
  eh_promocao?: boolean;
  description?: string | null;
  is_combo?: boolean;
  combo_items?: Array<{ product_id: string; name: string; quantity: number }>;
  menu_position?: number | null;
  image_path?: string;
  created_at: string;
  updated_at: string;
}

export interface Menu {
  [category: string]: Product[];
}

export interface MenuResponse {
  menu: Menu;
  category_order?: string[];
  total_products: number;
}

export interface CardapioOrderItem {
  product_id: string;
  quantity: number;
  product_variables?: { [key: string]: any };
}

export interface CreateCardapioOrderData {
  nome_cliente: string;
  telefone: string;
  endereco?: string;
  meio_pagamento: string;
  troco?: string;
  observacoes?: string;
  retirada_local?: boolean;
  items: CardapioOrderItem[];
}

export interface CardapioOrderResponse {
  success: boolean;
  order: any;
  daily_order_id: number;
  message: string;
  pix_mp_pending?: boolean;
  pix_token?: string;
}

export interface FreteInfo {
  can_deliver: boolean;
  distancia_km: number;
  valor_frete: number;
  max_distance: number;
}

/**
 * Obtem redirect para um link temporario a partir do slug do cardapio
 */
export const getCardapioRedirect = async (slug: string): Promise<CardapioRedirectResponse> => {
  try {
    const response = await cardapioApi.get(`/cardapio/${slug}/redirect`);
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      return error.response.data;
    }
    return {
      success: false,
      error: 'Erro ao acessar cardapio'
    };
  }
};

/**
 * Valida um link de cardapio temporario
 */
export const validateCardapioLink = async (linkId: string): Promise<CardapioLinkValidation> => {
  try {
    const response = await cardapioApi.get(`/cardapio-links/${linkId}/validate`);
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      return error.response.data;
    }
    return {
      valid: false,
      mode: 'cardapio',
      error: 'Erro ao validar link'
    };
  }
};

/**
 * Obtem o cardapio para um link de cardapio
 */
export const getCardapioMenu = async (linkId: string): Promise<MenuResponse> => {
  const response = await cardapioApi.get(`/cardapio-links/${linkId}/menu`);
  return response.data;
};

/**
 * Cria um pedido de cardapio (delivery - com telefone, nome, endereco)
 */
export const createCardapioOrder = async (
  linkId: string,
  orderData: CreateCardapioOrderData
): Promise<CardapioOrderResponse> => {
  const response = await cardapioApi.post(`/cardapio-links/${linkId}/order`, orderData);
  return response.data;
};

/**
 * Calcula o frete para um endereco
 */
export const calculateFrete = async (
  linkId: string,
  endereco: string,
  structured?: { bairro?: string; cidade?: string; estado?: string }
): Promise<FreteInfo> => {
  const response = await cardapioApi.post(`/cardapio-links/${linkId}/calculate-frete`, {
    endereco,
    ...structured
  });
  return response.data;
};

/**
 * Cria um pagamento PIX para um pedido
 */
export const createPixPayment = async (pixToken: string) => {
  const response = await cardapioApi.post('/pix/create', { pix_token: pixToken });
  return response.data;
};

/**
 * Consulta o status de um pagamento PIX
 */
export const getPixPaymentStatus = async (orderPaymentId: string) => {
  const response = await cardapioApi.get(`/pix/status/${orderPaymentId}`);
  return response.data;
};

export const cardapioAPI = {
  getCardapioRedirect,
  validateCardapioLink,
  getCardapioMenu,
  createCardapioOrder,
  calculateFrete,
};

export default cardapioAPI;
